export interface CongestionResult {
  center: [number, number];
  streetName: string;
  currentSpeed: number;
  freeFlowSpeed: number;
}

const GUADALAJARA_BBOX = '-3.23,40.60,-3.12,40.67';

// Probe points: main streets and intersections across the city
const FLOW_PROBE_POINTS: { name: string; lat: number; lon: number }[] = [
  { name: 'Plaza Mayor', lat: 40.6352, lon: -3.1720 },
  { name: 'Av. del Ejército', lat: 40.6331, lon: -3.1751 },
  { name: 'Glorieta Boixareu', lat: 40.6302, lon: -3.1790 },
  { name: 'C/ del Trabajo', lat: 40.6303, lon: -3.1645 },
  { name: 'C/ Laguna Grande', lat: 40.6315, lon: -3.1640 },
  { name: 'C/ Dos de Mayo', lat: 40.6385, lon: -3.1728 },
  { name: 'Paseo de la Estación', lat: 40.6390, lon: -3.1710 },
  { name: 'Av. de Venezuela', lat: 40.6345, lon: -3.1685 },
  { name: 'C/ Mayor', lat: 40.6360, lon: -3.1690 },
  { name: 'Av. de Barcelona', lat: 40.6370, lon: -3.1760 },
  { name: 'C/ Miguel Fluiters', lat: 40.6350, lon: -3.1660 },
  { name: 'C/ Cifuentes', lat: 40.6338, lon: -3.1710 },
];

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

interface FlowProbe {
  name: string;
  center: [number, number];
  currentSpeed: number;
  freeFlowSpeed: number;
  score: number; // lower = more interesting (slower traffic)
}

async function probeFlow(apiKey: string): Promise<FlowProbe[]> {
  const results = await Promise.allSettled(
    FLOW_PROBE_POINTS.map(async (pt) => {
      const url =
        `https://api.tomtom.com/traffic/services/4/flowSegmentData/relative0/15/json` +
        `?key=${apiKey}&point=${pt.lat},${pt.lon}`;
      const resp = await fetchWithTimeout(url, 3000);
      if (!resp.ok) return null;
      const data = await resp.json();
      const seg = data?.flowSegmentData;
      if (!seg) return null;
      // Score: combine absolute speed and ratio — lower = more congested
      // A street at 15 km/h (even if ratio=1.0) is more interesting than 50 km/h at ratio=0.9
      const ratio = seg.freeFlowSpeed > 0 ? seg.currentSpeed / seg.freeFlowSpeed : 1;
      const score = seg.currentSpeed * 0.6 + ratio * 40;
      return {
        name: pt.name,
        center: [pt.lon, pt.lat] as [number, number],
        currentSpeed: seg.currentSpeed,
        freeFlowSpeed: seg.freeFlowSpeed,
        score,
      };
    })
  );

  const probes: FlowProbe[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value) {
      // Discard closed/pedestrian roads: freeFlow < 10 km/h or currentSpeed=0 with no real flow
      const v = r.value;
      if (v.freeFlowSpeed < 10) continue;
      if (v.currentSpeed === 0 && v.freeFlowSpeed > 0) continue;
      probes.push(v);
    }
  }
  return probes;
}

async function tryIncidentJams(apiKey: string): Promise<CongestionResult | null> {
  const fields = encodeURIComponent(
    '{incidents{type,geometry{type,coordinates},properties{iconCategory,magnitudeOfDelay,delay,from,to}}}'
  );
  // Only real traffic events: 1=accident, 6=jam
  const url =
    `https://api.tomtom.com/traffic/services/5/incidentDetails` +
    `?key=${apiKey}&bbox=${GUADALAJARA_BBOX}&fields=${fields}` +
    `&language=es-ES&categoryFilter=1,6`;

  const resp = await fetchWithTimeout(url, 3000);
  if (!resp.ok) return null;

  const data = await resp.json();
  const incidents = data?.incidents;
  if (!Array.isArray(incidents) || incidents.length === 0) return null;

  // Pick highest delay
  incidents.sort((a: any, b: any) => (b.properties?.delay ?? 0) - (a.properties?.delay ?? 0));
  const best = incidents[0];
  const coords = best.geometry?.coordinates;
  if (!coords || coords.length === 0) return null;

  const mid = coords[Math.floor(coords.length / 2)];
  const center: [number, number] = best.geometry.type === 'Point'
    ? [coords[0], coords[1]]
    : [mid[0], mid[1]];

  return {
    center,
    streetName: best.properties?.from || best.properties?.to || 'zona centro',
    currentSpeed: 0,
    freeFlowSpeed: 0,
  };
}

export async function detectCongestion(apiKey: string): Promise<CongestionResult | null> {
  // Run both APIs in parallel
  const [flowProbes, jamResult] = await Promise.all([
    probeFlow(apiKey).catch(() => [] as FlowProbe[]),
    tryIncidentJams(apiKey).catch(() => null),
  ]);

  // Pick the slowest flow probe — only if there's real congestion (ratio < 0.7)
  if (flowProbes.length > 0) {
    flowProbes.sort((a, b) => a.score - b.score);
    const best = flowProbes[0];
    const ratio = best.freeFlowSpeed > 0 ? best.currentSpeed / best.freeFlowSpeed : 1;
    if (ratio < 0.7) {
      return {
        center: best.center,
        streetName: best.name,
        currentSpeed: best.currentSpeed,
        freeFlowSpeed: best.freeFlowSpeed,
      };
    }
  }

  // Fallback: real jam incident
  if (jamResult) return jamResult;

  return null;
}
